<template>
  <div style="width: 100%; height: 100%;">
    <template v-if="isLoading">
      <v-row no-gutters class="fill-height">
        <v-col cols="12">
          <main-loader />
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <div style="width: 100%; gap: 2rem" class="pa-6 d-flex flex-column">
        <div no-gutters class="d-flex align-center justify-space-between">
          <span>
            <base-button
              @click="handleSitesGeometries"
              :disabled="!sitesFeatures?.features.length"
              :translation-path="`table.rziDocuments.${this.areSitesVisible ? 'hideSites' : 'showSites'}`"
            ></base-button>
          </span>
          <span>
            <v-subheader class="delete-site-button text-caption pa-0 error--text" style="height: auto;">
              <a
                class="error--text d-flex align-center"
                @click="isDeleteDialogLoaded = isDeleteDialogVisible = true"
                style="gap: 0.2rem;"
              >
                <v-icon color="error" small>
                  mdi-delete
                </v-icon>
                <span>
                  {{ $i18n.t('table.rziDocuments.deleteProtectedSite') }}
                </span>
              </a>
            </v-subheader>
          </span>
        </div>
        <v-card elevation="2" class="pa-4">
          <v-row no-gutters style="gap: 1rem;">
            <v-col cols="4" style="max-width: 250px;">
              <img
                :src="getImageSource(mappedItemMetadata.image)"
                @error="handleImageError"
                style="width: 100%; height: 100%; object-fit: cover;"
              />
            </v-col>
            <v-col style="flex-grow: 1;">
              <div class="text-left d-flex flex-column justify-space-between" style="height: 100%; gap: 1rem">
                <div>
                  <div class="font-weight-medium">{{ mappedItemMetadata.title }}</div>
                  <div v-if="mappedItemMetadata.subtitle" class="text-caption text--secondary font-italic">
                    {{ item.subtitle }}
                  </div>
                </div>
                <div class="text-body-2">
                  <template v-for="(info, index) in mappedItemMetadata.cardInfo">
                    <div :key="index" :class="{ 'primary--text': info.primary }">
                      {{ info.attributeName }}: {{ info.value }}
                    </div>
                  </template>
                </div>
              </div>
            </v-col>
            <v-col cols="auto">
              <base-rounded-button
                icon="mdi-pencil"
                size="x-small"
                color="primary"
                :tooltip="$i18n.t('table.rziDocuments.editProtectedSite')"
                @click="isEditFeatureLoaded = isEditFeatureVisible = true"
              />
            </v-col>
          </v-row>
        </v-card>
        <v-card v-for="([key, data], index) in Object.entries(cardTables)" :key="index" elevation="2" class="pa-4">
          <rzi-joined-table
            :data="{ type: key, features: data }"
            :is-drawing-on.sync="isDrawingOn"
            :sticky-buttons.sync="stickyButtons"
            :current-metadata="currentMetadata"
            :current-site="item.id"
            :custom-datasource-schema="customSchemas[key] ?? null"
            @refreshTableDatasources="handleRefreshTableDatasources"
          />
        </v-card>
        <v-card elevation="2" class="pa-4">
          <rzi-scores
            :current-metadata="currentMetadata"
            :data-proxy="computedScoresData"
            :item="item"
            @retrieveItem="closeDialog => $emit('itemEdited', closeDialog)"
            @retrieveScores="retrieveScoresHandler"
          />
        </v-card>
      </div>
      <v-row
        v-if="isDrawingOn && stickyButtons.length"
        class="py-5 px-2 sticky-buttons d-flex"
        style="gap: 1rem"
        no-gutters
      >
        <v-col cols="auto" class="d-flex">
          <div
            v-for="(button, idx) of stickyButtons.filter(btn =>
              ['addMultipart', 'deleteMultipart'].includes(btn.value)
            )"
            :key="idx"
          >
            <base-rounded-button
              :icon="button.icon"
              size="x-small"
              :disabled="button.disabled"
              :is-active="button.active"
              v-bind="getButtonStyle(button.active)"
              :tooltip="button.tooltip"
              @click="button.action"
            />
          </div>
        </v-col>

        <v-col cols="auto" class="d-flex">
          <span
            v-for="(button, idx) of stickyButtons.filter(
              btn => !['addMultipart', 'deleteMultipart'].includes(btn.value)
            )"
            :key="idx"
          >
            <div>
              <base-button-subheader
                v-bind="{ ...button, cancel: button.cancel || button.cancelLike }"
                @click="button.action"
              />
            </div>
          </span>
        </v-col>
      </v-row>
    </template>
    <add-edit-feature-dialog
      v-if="isEditFeatureLoaded"
      :is-visible.sync="isEditFeatureVisible"
      :title="$i18n.t('table.rziDocuments.editProtectedSite')"
      v-bind="getEditFeatureData()"
      @done="closeDialog => $emit('itemEdited', closeDialog)"
    />
    <main-dialog
      :v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :loading="isDeleteDialogLoading"
      :title="$i18n.t('table.rziDocuments.deleteProtectedSite')"
      :body="$i18n.t('table.rziDocuments.deleteProtectedSiteBody')"
      @agree="handleDelete"
      agree-text="dialog.agreeDelete"
    />
  </div>
</template>

<script>
import { get, call, sync } from 'vuex-pathify';

import MainLoader from '@/components/MainLoader';
import RziJoinedTable from '@/components/RziJoinedTable';
import AddEditFeatureDialog from '@/components/AddEditFeatureDialog';
import RziScores from '@/components/RziScores';
import { utils } from '@/mixins/rziHelpers';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'RziDocumentationItemSection',
  mixins: [prepareAttributesFeatureCardMixin, utils],
  components: {
    MainLoader,
    RziJoinedTable,
    RziScores,
    AddEditFeatureDialog,
    MainDialog: () => import('@/components/MainDialog'),
  },
  props: {
    currentMetadata: {
      type: Object,
    },
    item: {
      type: Object,
      required: true,
    },
    tabInfo: {
      type: Object,
    },
  },
  data: () => ({
    isLoading: true,
    cardTables: {},
    scores: null,
    sitesFeatures: {},
    isDrawingOn: false,
    stickyButtons: [],
    customSchemas: {},
    areSitesVisible: false,
    isEditFeatureLoaded: false,
    isEditFeatureVisible: false,
    isDeleteDialogLoaded: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoading: false,
  }),
  watch: {
    item: {
      immediate: true,
      handler(nV, oV) {
        if (nV?.id !== oV?.id) this.init();
      },
    },
  },
  computed: {
    tableHeight: sync('table/tableHeight'),
    dataSources: get('layers/metadata'),
    rziScores: sync('rziData/scores'),
    layersMetadata: get('layers/metadata'),
    sitesTypesFeatures: sync('rziData/sitesTypesFeatures'),
    mappedItemMetadata() {
      const attributes = this.currentMetadata.protectedSites.attributes;
      const datasourceAttributes =
        this.dataSources[this.currentMetadata.protectedSites.datasource]?.attributes_schema.attributes || [];
      return {
        ...Object.fromEntries(
          ['title', 'subtitle', 'code', 'image', 'type'].map(name => [
            name,
            this.getAttributeTextValueByName(
              this.item.feature.properties[attributes[name]],
              attributes[name],
              this.currentMetadata.protectedSites.datasource
            ),
          ])
        ),
        cardInfo: [attributes.code, attributes.sdf_score, attributes.gios_score, attributes.condition].map(
          attrName => ({
            primary: attrName === attributes.code,
            attributeName: datasourceAttributes.find(attr => attr.name === attrName)?.verbose_name,
            value: this.getAttributeTextValueByName(
              this.item.feature.properties[attrName],
              attrName,
              this.currentMetadata.protectedSites.datasource
            ),
          })
        ),
      };
    },
    computedScoresData() {
      return {
        ...this.scores,
        main: ['gios_score', 'notes'].reduce((acc, item) => {
          acc[this.currentMetadata.protectedSites.attributes[item]] = this.item.feature.properties[
            this.currentMetadata.protectedSites.attributes[item]
          ];
          return acc;
        }, {}),
      };
    },
  },
  methods: {
    deleteDatasourceFeature: call('layers/deleteDatasourceFeature'),
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    handleImageError(er) {
      er.target.src = this.getImagePlaceholder();
    },
    getButtonStyle(isActive) {
      return isActive
        ? { color: this.$_colors.primarybackground, iconColor: this.$_colors.primary }
        : {
            color: 'rgba(225,225,225,1)',
            iconColor: 'rgba(0,0,0,0.7)',
          };
    },
    getEditFeatureData() {
      return {
        featureData: {
          id: this.item.id,
          datasource: this.currentMetadata.protectedSites.datasource,
        },
        featureMetadata: this.item.feature,
        hiddenAttributes: ['_systems', ...this.currentMetadata.protectedSites.hiddenAttributes],
        staticProperties: {
          ...['title', 'gios_score', 'notes'].reduce((acc, item) => {
            const attrName = this.currentMetadata.protectedSites.attributes[item];
            acc[attrName] = this.item.feature.properties[attrName];
            return acc;
          }, {}),
          [this.currentMetadata.protectedSites.attributes.docsId]: this.tabInfo.documentFeature.id,
        },
      };
    },
    handleSitesGeometries() {
      if (this.tableHeight === 3) this.tableHeight = 2;
      setTimeout(() => {
        this.areSitesVisible = !this.areSitesVisible;
        this.$root.$emit('toggleCustomVectorLayer', {
          id: 'all-sites-features',
          ...(this.areSitesVisible && { geometry: this.sitesFeatures, fitView: true }),
        });
      }, 0);
    },
    async handleDelete() {
      this.isDeleteDialogLoading = true;
      try {
        await this.deleteDatasourceFeature({
          datasource: this.currentMetadata.protectedSites.datasource,
          id: this.item.id,
          params: {
            cascade: true,
          },
        });
        this.$emit('deleteSite', () => {
          this.isDeleteDialogVisible = this.isDeleteDialogLoading = false;
        });
      } catch {
        this.isDeleteDialogLoading = false;
      }
    },
    async retrieveScoresHandler(type, endCallback) {
      await this.retrieveScores(type, endCallback, data => {
        /**
         * Custom objectives schema
         */
        let newDatasourceAttributes = JSON.parse(
          JSON.stringify(
            this.layersMetadata[this.currentMetadata.tableViews.objectives.datasources.none]?.attributes_schema
              .attributes
          )
        );
        [
          ['parameter', data.rawParameters.features],
          ['index', data.rawIndexes.features],
        ].forEach(([attributeName, items]) => {
          const attributeData = newDatasourceAttributes.find(
            attr => attr.name === this.currentMetadata.tableViews.objectives.attributes[attributeName]
          );
          attributeData.relation.filter_expression = JSON.stringify({
            $IN: {
              [`${attributeData.relation.data_source}.${attributeData.relation.attribute}`]: items.map(ft => ft.id),
            },
          });
        });
        this.customSchemas.objectives = newDatasourceAttributes;
        /** */
      });
    },
    async init() {
      this.isLoading = true;
      this.areSitesVisible = false;
      this.$root.$emit('toggleCustomVectorLayer', { id: 'all-sites-features' });
      try {
        const tablesDatasources = Object.values(this.currentMetadata.tableViews).reduce((acc, item) => {
          Object.entries(item.datasources).forEach(([type, dt]) => {
            let founditem = acc.find(it => it[1] === dt);
            if (!founditem) {
              acc.push([type, dt]);
            }
          });
          return acc;
        }, []);
        const promises = tablesDatasources.reduce((acc, datasource) => {
          acc = [...acc, this.getSingleDatasourceFeatures(datasource[1], datasource[0])];
          return acc;
        }, []);
        const [scores, sites, ...results] = await Promise.all([
          ...(this.rziScores.length
            ? [new Promise(resolve => resolve(null))]
            : [this.getDataSourceFeatures({ dataSource: this.currentMetadata.score.dictGiosScores.datasource })]),
          Promise.all(
            this.currentMetadata.sitePlacs.datasources.map(dt => {
              return this.getDataSourceFeatures({
                dataSource: dt,
                filters: {
                  $EQUAL: {
                    [`${dt}.${this.currentMetadata.sitePlacs.filterAttribute}`]: {
                      value: this.item.feature.id,
                    },
                  },
                },
              });
            })
          ),
          ...promises,
        ]);
        if (scores)
          this.rziScores = {
            scores,
            currentMetadata: this.currentMetadata,
          };
        this.sitesFeatures = sites.reduce(
          (acc, item) => {
            return {
              ...acc,
              features: [...acc.features, ...item.features],
            };
          },
          {
            type: 'FeatureCollection',
            features: [],
            crs: sites[0].crs,
          }
        );
        this.cardTables = results.flat().reduce((acc, item) => {
          acc[item[0]] = acc[item[0]] ? [...acc[item[0]], ...item[1]] : [...item[1]];
          return acc;
        }, {});
        await this.retrieveScoresHandler();
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },
  },
  beforeDestroy() {
    this.$root.$emit('toggleCustomVectorLayer', { id: 'all-sites-features' });
  },
};
</script>

<style lang="scss" scoper>
.delete-site-button:hover span {
  text-decoration: underline;
}
.sticky-buttons {
  background-color: white;
  z-index: 1;
  box-shadow: 0px -3px 6px rgba(0, 0, 0, 0.15);

  width: 100%;
  position: sticky;
  bottom: 0px;
}
</style>
